import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { CountrySelectService } from 'gain-lib/country-select';
import { BehaviorSubject, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

export class GaCountrySelectChange {
  public readonly source: GaCountrySelectComponent;
  public readonly value: string[];

  constructor(source: GaCountrySelectComponent, value: string[]) {
    this.source = source;
    this.value = value;
  }
}

@Component({
  selector: 'gax-country-select',
  templateUrl: './country-select.component.html',
  styleUrls: ['./country-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GaCountrySelectComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject();
  protected displayValue$ = new BehaviorSubject<string | null>(null);

  @Input()
  label = 'Countries';

  @Input()
  placeholder = 'Select countries';

  @Input()
  dialogTitle?: string;

  @Input()
  countryWhitelist?: string[];

  @Input()
  readonlyCountries: string[] = [];

  @Input()
  unsupportedCountries: string[] = [];

  @Input()
  set disabled(disable: boolean) {
    if (disable && !this.disabled) {
      this.countriesFormControl.disable();
    }
    if (!disable && this.disabled) {
      this.countriesFormControl.enable();
    }
  }

  get disabled() {
    return this.countriesFormControl.disabled;
  }

  @Input()
  set enabled(enable: boolean) {
    this.disabled = !enable;
  }

  get enabled() {
    return !this.disabled;
  }

  @Input()
  readOnlyReasonTooltip?: string = '';

  @Input()
  readonly countriesFormControl: FormControl<string[]> = new FormControl<
    string[]
  >([], {
    nonNullable: true,
  });

  @Input()
  set required(required: boolean) {
    if (required && !this.required) {
      this.countriesFormControl.addValidators(Validators.required);
    }

    if (!required && this.required) {
      this.countriesFormControl.removeValidators(Validators.required);
    }
    this.countriesFormControl.updateValueAndValidity();
  }

  get required() {
    return this.countriesFormControl.hasValidator(Validators.required);
  }

  requiredErrorMessage = input<string>('At least one country must be selected');

  @Input()
  set value(value: string[]) {
    this._setCountries(value, { emitEvent: false });
  }

  @Output()
  selectionChange = new EventEmitter<GaCountrySelectChange>();

  get countries() {
    if (this.countriesFormControl == null) {
      throw new Error('missing form control');
    }
    return this.countriesFormControl;
  }

  constructor(
    private _countrySelect: CountrySelectService,
    private _cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.countries.valueChanges
      .pipe(startWith(this.countries.value), takeUntil(this.onDestroy$))
      .subscribe(() => {
        this._refreshDisplayValue();
      });
  }

  private _refreshDisplayValue() {
    this.displayValue$.next(this.getDisplayValue());
  }

  getDisplayValue() {
    const displayValue =
      this.countries.value.length === 1
        ? '1 country selected'
        : this.countries.value.length > 1
          ? `${this.countries.value.length} countries selected`
          : '';
    return displayValue;
  }

  public async selectCountries() {
    const result = await this._countrySelect.selectCountries({
      countryWhitelist: this.countryWhitelist,
      readonlyCountries: this.readonlyCountries,
      selectedCountries: this.countries.value,
      dialogTitle: this.dialogTitle,
      unsupportedCountries: this.unsupportedCountries,
    });
    if (!result.cancelled && 'countries' in result) {
      this._setCountries(result.countries);
      this.countriesFormControl.markAsTouched();
    }
    this.countriesFormControl.updateValueAndValidity();
  }

  protected async _openCountrySelectDialog(ev: MouseEvent | Event) {
    ev.preventDefault();
    await this.selectCountries();
  }

  private _setCountries(
    countries: string[],
    options?: { emitEvent?: boolean },
  ) {
    this.countries.setValue(countries);
    if (options?.emitEvent !== false) {
      this.selectionChange.emit(new GaCountrySelectChange(this, countries));
    }
  }
  ngOnDestroy() {
    this.onDestroy$.next(undefined);
    this.onDestroy$.complete();
  }
}
